import { z } from "zod";

import { CardDefinitionSchema, type CardDefinition } from "./card-schema.js";
import { SCHEMA_VERSION, SemanticVersionSchema, type CardDefinitionId } from "./primitives.js";
import {
  ConditionDefinitionSchema,
  EffectDefinitionSchema,
  TriggerDefinitionSchema,
} from "./references.js";

function addDuplicateIdIssues(
  items: readonly { readonly id: string }[],
  path: string,
  context: z.RefinementCtx,
): void {
  const firstIndexById = new Map<string, number>();

  items.forEach((item, index) => {
    const firstIndex = firstIndexById.get(item.id);
    if (firstIndex === undefined) {
      firstIndexById.set(item.id, index);
      return;
    }

    context.addIssue({
      code: "custom",
      path: [path, index, "id"],
      message: `duplicates ${path}[${firstIndex}].id '${item.id}'`,
    });
  });
}

interface ReferenceSets {
  readonly effects: ReadonlySet<string>;
  readonly conditions: ReadonlySet<string>;
  readonly triggers: ReadonlySet<string>;
}

function requireEffectReference(
  effectId: string,
  path: readonly (string | number)[],
  references: ReferenceSets,
  context: z.RefinementCtx,
): void {
  if (!references.effects.has(effectId)) {
    context.addIssue({
      code: "custom",
      path: [...path, "effectId"],
      message: `references unknown effect '${effectId}'`,
    });
  }
}

function requireConditionReference(
  conditionId: string,
  path: readonly (string | number)[],
  references: ReferenceSets,
  context: z.RefinementCtx,
): void {
  if (!references.conditions.has(conditionId)) {
    context.addIssue({
      code: "custom",
      path: [...path, "conditionId"],
      message: `references unknown condition '${conditionId}'`,
    });
  }
}

function requireTriggerReference(
  triggerId: string,
  path: readonly (string | number)[],
  references: ReferenceSets,
  context: z.RefinementCtx,
): void {
  if (!references.triggers.has(triggerId)) {
    context.addIssue({
      code: "custom",
      path: [...path, "triggerId"],
      message: `references unknown trigger '${triggerId}'`,
    });
  }
}

function validateCardReferences(
  card: CardDefinition,
  cardIndex: number,
  references: ReferenceSets,
  context: z.RefinementCtx,
): void {
  const cardPath = ["cards", cardIndex] as const;

  switch (card.type) {
    case "land":
      card.stages.forEach((stage, stageIndex) => {
        stage.effects.forEach((effect, effectIndex) => {
          requireEffectReference(
            effect.effectId,
            [...cardPath, "stages", stageIndex, "effects", effectIndex],
            references,
            context,
          );
        });
      });
      break;

    case "commander":
      card.mandates.forEach((mandate, mandateIndex) => {
        requireConditionReference(
          mandate.condition.conditionId,
          [...cardPath, "mandates", mandateIndex, "condition"],
          references,
          context,
        );
        mandate.effects.forEach((effect, effectIndex) => {
          requireEffectReference(
            effect.effectId,
            [...cardPath, "mandates", mandateIndex, "effects", effectIndex],
            references,
            context,
          );
        });
      });
      break;

    case "army":
      card.effects.forEach((effect, effectIndex) => {
        requireEffectReference(
          effect.effectId,
          [...cardPath, "effects", effectIndex],
          references,
          context,
        );
      });
      break;

    case "event":
      card.resolution.effects.forEach((effect, effectIndex) => {
        requireEffectReference(
          effect.effectId,
          [...cardPath, "resolution", "effects", effectIndex],
          references,
          context,
        );
      });
      if (card.enhancedResolution !== undefined) {
        requireConditionReference(
          card.enhancedResolution.condition.conditionId,
          [...cardPath, "enhancedResolution", "condition"],
          references,
          context,
        );
        card.enhancedResolution.effects.forEach((effect, effectIndex) => {
          requireEffectReference(
            effect.effectId,
            [...cardPath, "enhancedResolution", "effects", effectIndex],
            references,
            context,
          );
        });
      }
      break;

    case "policy":
      card.effects.forEach((effect, effectIndex) => {
        requireEffectReference(
          effect.effectId,
          [...cardPath, "effects", effectIndex],
          references,
          context,
        );
      });
      break;

    case "conspiracy":
      requireTriggerReference(
        card.trigger.triggerId,
        [...cardPath, "trigger"],
        references,
        context,
      );
      card.resolution.effects.forEach((effect, effectIndex) => {
        requireEffectReference(
          effect.effectId,
          [...cardPath, "resolution", "effects", effectIndex],
          references,
          context,
        );
      });
      break;
  }
}

export const CardCatalogSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    cardDataVersion: SemanticVersionSchema,
    cards: z.array(CardDefinitionSchema),
    effects: z.array(EffectDefinitionSchema),
    conditions: z.array(ConditionDefinitionSchema),
    triggers: z.array(TriggerDefinitionSchema),
  })
  .strict()
  .superRefine((catalog, context) => {
    addDuplicateIdIssues(catalog.cards, "cards", context);
    addDuplicateIdIssues(catalog.effects, "effects", context);
    addDuplicateIdIssues(catalog.conditions, "conditions", context);
    addDuplicateIdIssues(catalog.triggers, "triggers", context);

    const references: ReferenceSets = {
      effects: new Set(catalog.effects.map((effect) => effect.id)),
      conditions: new Set(catalog.conditions.map((condition) => condition.id)),
      triggers: new Set(catalog.triggers.map((trigger) => trigger.id)),
    };

    catalog.cards.forEach((card, cardIndex) => {
      validateCardReferences(card, cardIndex, references, context);
    });
  });

export function validateCardCatalog(input: unknown) {
  return CardCatalogSchema.safeParse(input);
}

export function getCardDefinition(
  catalog: CardCatalog,
  cardId: CardDefinitionId,
): CardDefinition | undefined {
  return catalog.cards.find((card) => card.id === cardId);
}

export type CardCatalog = z.infer<typeof CardCatalogSchema>;
export type CardCatalogValidationResult = ReturnType<typeof validateCardCatalog>;
